import {
  getNotificationList,
  markNotificationAsRead,
  NotificationItem,
} from '@/services/notification';
import { history } from '@umijs/max';
import { Button, Empty, Radio, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './NotificationPopover.less';

const PAGE_SIZE = 25;

type DateFilterType = 'today' | 'yesterday' | '7days' | '30days' | null;

interface NotificationPopoverProps {
  open: boolean;
  onClose?: () => void;
}

const NotificationPopover: React.FC<NotificationPopoverProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterType>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get date range based on filter
  const getDateRange = useCallback(
    (filter: DateFilterType): { fromDate?: string; toDate?: string } => {
      const today = dayjs();
      let fromDate: string | undefined;
      let toDate: string | undefined;

      if (filter === 'today') {
        fromDate = today.format('YYYY-MM-DD');
        toDate = today.format('YYYY-MM-DD');
      } else if (filter === 'yesterday') {
        const yesterday = today.subtract(1, 'day');
        fromDate = yesterday.format('YYYY-MM-DD');
        toDate = yesterday.format('YYYY-MM-DD');
      } else if (filter === '7days') {
        fromDate = today.subtract(7, 'day').format('YYYY-MM-DD');
        toDate = today.format('YYYY-MM-DD');
      } else if (filter === '30days') {
        fromDate = today.subtract(30, 'day').format('YYYY-MM-DD');
        toDate = today.format('YYYY-MM-DD');
      }

      return { fromDate, toDate };
    },
    []
  );

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (page: number = 0, isLoadMore: boolean = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const dateRange = getDateRange(dateFilter);
        const response = await getNotificationList({
          pageIndex: page,
          pageSize: PAGE_SIZE,
          keyword: '',
          isRead: activeTab === 'unread' ? 0 : null,
          isMark: null,
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
          pageSorts: null,
        });

        if (response.success) {
          if (isLoadMore) {
            setNotifications(prev => [...prev, ...response.data]);
          } else {
            setNotifications(response.data);
          }
          setTotal(response.total);
          setPageIndex(page);
          setHasMore(response.data.length === PAGE_SIZE && (page + 1) * PAGE_SIZE < response.total);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [activeTab, dateFilter, getDateRange]
  );

  // Initial load or when filters change
  useEffect(() => {
    if (open) {
      setPageIndex(0);
      setNotifications([]);
      setHasMore(true);
      fetchNotifications(0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, dateFilter]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (isNearBottom) {
      fetchNotifications(pageIndex + 1, true);
    }
  }, [loadingMore, hasMore, pageIndex, fetchNotifications]);

  // Group notifications by date
  const groupNotificationsByDate = (items: NotificationItem[]) => {
    const groups: Record<string, NotificationItem[]> = {};
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');

    items.forEach(item => {
      const itemDate = dayjs(item.notifiedAt);
      let groupKey: string;

      if (itemDate.isSame(today, 'day')) {
        groupKey = 'today';
      } else if (itemDate.isSame(yesterday, 'day')) {
        groupKey = 'yesterday';
      } else {
        groupKey = itemDate.format('DD/MM/YYYY');
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  };

  // Format date label
  const getDateLabel = (key: string): string => {
    if (key === 'today') return 'Hôm nay';
    if (key === 'yesterday') return 'Hôm qua';
    return key;
  };

  // Format time ago
  const getTimeAgo = (dateString: string): string => {
    const date = dayjs(dateString);
    const now = dayjs();
    const diffMinutes = now.diff(date, 'minute');
    const diffHours = now.diff(date, 'hour');
    const diffDays = now.diff(date, 'day');

    if (diffMinutes < 1) {
      return `Vừa xong`;
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} phút trước`;
    } else if (diffHours < 12) {
      return `${diffHours} giờ trước`;
    } else {
      return `${date.format('DD/MM/YYYY')} lúc ${date.format('HH:mm')}`;
    }
  };

  // Parse payload to get URL
  const getNotificationUrl = (notification: NotificationItem): string | null => {
    try {
      if (!notification.payload) return null;
      const payload = JSON.parse(notification.payload);
      return payload.url || null;
    } catch (error) {
      console.error('Error parsing notification payload:', error);
      return null;
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark as read if unread (status === 0)
    if (notification.status === 0) {
      try {
        await markNotificationAsRead(notification.id);
        // Update local state
        setNotifications(prev => {
          // If on "unread" tab, remove the notification from list
          if (activeTab === 'unread') {
            return prev.filter(item => item.id !== notification.id);
          }
          // Otherwise, just update the status
          return prev.map(item => (item.id === notification.id ? { ...item, status: 1 } : item));
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    const url = getNotificationUrl(notification);
    if (url) {
      history.push(url);
      // Close popover after navigation
      if (onClose) {
        onClose();
      }
    }
  };

  const groupedNotifications = groupNotificationsByDate(notifications);
  const sortedDateKeys = Object.keys(groupedNotifications).sort((a, b) => {
    if (a === 'today') return -1;
    if (b === 'today') return 1;
    if (a === 'yesterday') return -1;
    if (b === 'yesterday') return 1;
    return dayjs(b, 'DD/MM/YYYY').valueOf() - dayjs(a, 'DD/MM/YYYY').valueOf();
  });

  return (
    <div className='notification-popover-content'>
      <div className='notification-popover-header'>Thông báo & Nhắc nhở</div>
      <div style={{ padding: '0 16px' }}>
        <Segmented
          value={activeTab}
          onChange={value => setActiveTab(value as 'all' | 'unread')}
          options={[
            { label: 'Tất cả', value: 'all' },
            { label: 'Chưa đọc', value: 'unread' },
          ]}
          block
          style={{ marginBottom: 16 }}
        />
      </div>

      {/* Date Filters */}
      <div style={{ padding: '0 16px' }}>
        <div className='notification-date-filters'>
          <div className='filter-header'>
            <span>Lọc theo ngày</span>
            {dateFilter && (
              <Button
                type='link'
                size='small'
                onClick={() => setDateFilter(null)}
                style={{ padding: 0, height: 'auto' }}
              >
                Xóa bộ lọc
              </Button>
            )}
          </div>
          <div className='filter-buttons'>
            <Radio.Group
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              buttonStyle='solid'
            >
              {/* <Radio.Button value='today'>Hôm nay</Radio.Button> */}
              <Radio.Button value='yesterday'>Hôm qua</Radio.Button>
              <Radio.Button value='7days'>7 ngày</Radio.Button>
              <Radio.Button value='30days'>30 ngày</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div style={{ padding: '0 16px' }}>
        <div
          className='notification-list-container'
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {loading && notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin />
            </div>
          ) : notifications.length === 0 ? (
            <Empty description='Không có thông báo' style={{ margin: '40px 0' }} />
          ) : (
            <>
              {sortedDateKeys.map(dateKey => (
                <div key={dateKey} className='notification-date-group'>
                  <div className='date-group-header'>{getDateLabel(dateKey)}</div>
                  {groupedNotifications[dateKey].map(notification => {
                    const url = getNotificationUrl(notification);
                    return (
                      <div
                        key={notification.id}
                        className='notification-item'
                        onClick={() => handleNotificationClick(notification)}
                        style={{ cursor: url ? 'pointer' : 'default' }}
                      >
                        <div className='notification-content'>
                          <div className='notification-title'>{notification.subject}</div>
                          <div className='notification-description'>{notification.description}</div>
                          <div className='notification-time'>
                            {getTimeAgo(notification.notifiedAt)}
                          </div>
                        </div>
                        {/* Show unread indicator - status 0 typically means unread */}
                        {notification.status === 0 && <div className='notification-unread-dot' />}
                      </div>
                    );
                  })}
                </div>
              ))}
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <Spin size='small' />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPopover;
